import {useCallback, useMemo, useState} from 'react';

import {BarChart} from '../../../../../../layout/chart/bar-chart/bar-chart';
import {BarDataSetType, BarDataType} from '../../../../../../layout/chart/bar-chart/bar-chart-type';
import {getStripesPattern} from '../../../../../../layout/chart/patterns/stripes';
import {useLocale} from '../../../../../../provider/locale/locale-hook';
import {
    ActionDynamicsEnum,
    OnlinePresenceActionDynamicsDataType,
    OnlinePresenceActionDynamicsType,
} from '../../../../../../service/online-presence/online-presence-type';
import {getEnumValue} from '../../../../../../util/enum';
import {DATASET_STACK__COMPARE} from '../../../../reviews-analysis/pages/dashboard/widgets/sentiment-by-time/sentiment-by-time-chart/sentiment-by-time-chart-helper';
import {
    getTargetActionDynamicsChartOptions,
    sortActionDynamicsValues,
    targetActionsColorMap,
    targetActionsLangKeyMap,
} from '../../target-actions-dynamics-helper';
import {TargetActionsLegend} from '../target-actions-legend/target-actions-legend';

import * as styles from './target-actions-chart.scss';

type PropsType = {
    labels: Array<string>;
    comparedLabels: Array<string>;
    data: OnlinePresenceActionDynamicsType;
    allowedKeys: Array<ActionDynamicsEnum>;
    isCompareMode: boolean;
    staticChart?: boolean;
    pdfPreview: boolean;
};

export function TargetActionsChart(props: PropsType): JSX.Element {
    const {data, isCompareMode, labels, comparedLabels, staticChart, pdfPreview, allowedKeys} = props;

    const {getLocalizedString} = useLocale();

    const [legendFilter, setLegendFilter] = useState<Array<ActionDynamicsEnum>>(allowedKeys);
    const [isCompareLegendActive, setIsCompareLegendActive] = useState<boolean>(true);

    const getTargetActionsDynamicsDatasets = useCallback(
        (periodData?: Array<OnlinePresenceActionDynamicsDataType>, isCompare = false): Array<BarDataSetType> => {
            if (!periodData) {
                return [];
            }

            return periodData
                .flatMap((dataItem) => {
                    const dataItemKey = getEnumValue(ActionDynamicsEnum, dataItem.label);

                    if (!dataItemKey || !allowedKeys.includes(dataItemKey)) {
                        return [];
                    }

                    return [
                        {
                            ...dataItem,
                            label: dataItemKey,
                        },
                    ];
                })
                .filter((dataItem) => {
                    return legendFilter.includes(dataItem.label);
                })
                .sort(sortActionDynamicsValues)
                .map((dataItem) => {
                    return {
                        label: getLocalizedString(targetActionsLangKeyMap[dataItem.label]),
                        values: dataItem.values,
                        ...(isCompare
                            ? {
                                  stack: DATASET_STACK__COMPARE,
                                  color: getStripesPattern(targetActionsColorMap[dataItem.label]),
                              }
                            : {
                                  color: targetActionsColorMap[dataItem.label],
                              }),
                    };
                });
        },
        [allowedKeys, getLocalizedString, legendFilter]
    );

    const targetActionsDynamicsData: BarDataType = useMemo(
        () => ({
            labels,
            datasets: [
                ...getTargetActionsDynamicsDatasets(data.bar.current),
                ...(isCompareLegendActive ? getTargetActionsDynamicsDatasets(data.bar.compared, true) : []),
            ],
        }),
        [data.bar, getTargetActionsDynamicsDatasets, isCompareLegendActive, labels]
    );

    return (
        <div>
            <TargetActionsLegend
                allowedKeys={allowedKeys}
                data={data}
                isCompareLegendActive={isCompareLegendActive}
                isCompareMode={isCompareMode}
                legendFilter={legendFilter}
                setIsCompareLegendActive={setIsCompareLegendActive}
                setLegendFilter={setLegendFilter}
                staticChart={staticChart}
            />
            <div className={styles.TargetActionsChart}>
                <BarChart
                    data={targetActionsDynamicsData}
                    options={getTargetActionDynamicsChartOptions({
                        tooltipOptions: {
                            data: {
                                datasets: getTargetActionsDynamicsDatasets(data.bar.current),
                                labels: targetActionsDynamicsData.labels,
                            },
                            ...(isCompareMode
                                ? {
                                      compareData: {
                                          datasets: getTargetActionsDynamicsDatasets(data.bar.compared),
                                          labels: comparedLabels,
                                      },
                                  }
                                : {}),
                            showSummary: true,
                            showCompareValuesDynamics: isCompareMode,
                        },
                        showTooltip: !staticChart,
                        disableAnimation: pdfPreview,
                    })}
                />
            </div>
        </div>
    );
}
